import { useEffect, useRef, useState } from 'react';
import { uniqBy } from 'lodash';
import { getWorkspaceId, useAppSelector } from '@zonofi/zono-money-store';
import { PaginationMeta } from '../types';

interface UsePaginatedListProps<T> {
  fetchDataApiCall: (params: { pageNo: number; pageSize: number }) => void;
  apiResponseData: T[];
  isLoading: boolean;
  isError?: boolean;
  uniqueKey: keyof T;
  dependencies?: any[];
  pageSize?: number;
  paginationMeta?: PaginationMeta;
}

const DEFAULT_PAGE_SIZE = 20;

export const usePaginatedList = <T>({
  fetchDataApiCall,
  apiResponseData = [],
  isLoading = false,
  isError = false,
  uniqueKey,
  dependencies = [],
  pageSize = DEFAULT_PAGE_SIZE,
  paginationMeta,
}: UsePaginatedListProps<T>) => {
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);

  const [data, setData] = useState<T[]>([]);
  const [pageNo, setPageNo] = useState(1);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const lastItemRef = useRef<HTMLDivElement | null>(null);
  const fetchedPagesRef = useRef<Set<number>>(new Set());
  const scrollTimeout = useRef<NodeJS.Timeout | number | null>(null);
  const paginationLock = useRef(false);
  const isResettingRef = useRef(false);

  const onScroll = () => {
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

    scrollTimeout.current = setTimeout(() => {
      const node = lastItemRef.current;
      const justReset = pageNo === 1 && data.length === 0;

      if (
        node &&
        !isLoading &&
        !isPageLoading &&
        !justReset &&
        !paginationLock.current &&
        hasMoreData &&
        !fetchedPagesRef.current.has(pageNo + 1)
      ) {
        const rect = node.getBoundingClientRect();
        const reachedBottom = rect.bottom - 50 <= window.innerHeight;
        if (
          reachedBottom ||
          (pageSize !== DEFAULT_PAGE_SIZE &&
            paginationMeta &&
            pageSize * pageNo < paginationMeta.totalCount)
        ) {
          paginationLock.current = true;
          setIsPageLoading(true);
          setPageNo((prev) => prev + 1);
        }
      }
    }, 200);
  };

  useEffect(() => {
    try {
      resetAll();
      fetchDataApiCall({ pageNo: 1, pageSize });
    } catch (e) {
      console.error('Error fetching data:', e);
    }
  }, [sellerWorkspaceId, refreshNonce, ...dependencies]);

  useEffect(() => {
    isResettingRef.current = false;
    if (pageNo === 1) return;
    if (
      !isResettingRef.current &&
      !fetchedPagesRef.current.has(pageNo) &&
      hasMoreData &&
      !isError &&
      !isLoading
    ) {
      fetchDataApiCall({ pageNo, pageSize });
    }
  }, [pageNo]);

  const checkAndLoadMoreData = () => {
    const node = lastItemRef.current;

    if (
      node &&
      !isLoading &&
      !isPageLoading &&
      !paginationLock.current &&
      hasMoreData &&
      !fetchedPagesRef.current.has(pageNo + 1)
    ) {
      const rect = node.getBoundingClientRect();
      const isContentBelowViewport = rect.bottom > window.innerHeight;

      if (
        !isContentBelowViewport ||
        (pageSize !== DEFAULT_PAGE_SIZE &&
          paginationMeta &&
          paginationMeta.endRecord < paginationMeta.totalCount)
      ) {
        paginationLock.current = true;
        setIsPageLoading(true);
        setPageNo((prev) => prev + 1);
      }
    }
  };

  useEffect(() => {
    if (isLoading) return;

    if (isError) {
      setIsPageLoading(false);
      paginationLock.current = false;
      setHasMoreData(false);
      return;
    }

    const currentPageItemCount = apiResponseData.length;

    setData((prevData) => {
      const merged =
        pageNo === 1 ? apiResponseData : [...prevData, ...apiResponseData];
      return uniqBy(merged, uniqueKey as string);
    });

    fetchedPagesRef.current.add(pageNo);

    let hasMore = true;

    if (paginationMeta) {
      const { endRecord, totalCount } = paginationMeta;
      hasMore = endRecord < totalCount;
    } else {
      hasMore = currentPageItemCount === pageSize;
    }
    setHasMoreData(hasMore);

    setIsPageLoading(false);
    paginationLock.current = false;

    setTimeout(() => {
      checkAndLoadMoreData();
    }, 100);
  }, [apiResponseData, isLoading, isError, pageNo]);

  const resetAll = () => {
    isResettingRef.current = true;
    setPageNo(1);
    setIsPageLoading(false);
    fetchedPagesRef.current.clear();
    paginationLock.current = false;
  };

  const refresh = () => {
    setRefreshNonce((prev) => prev + 1);
  };

  return {
    data,
    pageNo,
    lastItemRef,
    onScroll,
    resetAll,
    refresh,
    isPageLoading,
    hasMoreData,
    isEndOfList: !hasMoreData,
  };
};
